import { Component } from '@theme/component'
import {
  CartUpdateEvent,
  DiscountUpdateEvent,
  ThemeEvents
} from '@theme/events'
import { fetchConfig } from '@theme/maths'
import { cartPerformance } from '@theme/performance'
import { morphSection, sectionRenderer } from '@theme/section-renderer'

const CartDrawerItemsConfig = {
  debounceDelay: 300
}

class CartItemsComponent extends Component {
  #pendingLineChanges = new Map()

  #queueLineChange = (event) => {
    if (!(event.target instanceof Node && this.contains(event.target))) return

    const line = event.detail?.cartLine

    if (!line) return

    clearTimeout(this.#pendingLineChanges.get(line))
    this.#pendingLineChanges.set(
      line,
      setTimeout(() => {
        this.#pendingLineChanges.delete(line)
        this.#onQuantityChange(event)
      }, CartDrawerItemsConfig.debounceDelay)
    )
  }

  handleDiscountUpdate = (event) => {
    const { sourceId } = event.detail
    if (typeof sourceId === 'string' && sourceId === this.sectionId) {
      return
    }

    this.#handleCartUpdate(event)
  }

  #handleCartError = (line, parsedResponseText) => {
    const quantityInput = this.querySelector(`[data-cart-line="${line}"]`)

    if (!quantityInput) throw new Error('Quantity input not found')

    quantityInput.value = quantityInput.defaultValue

    const cartItemError = this.refs[`cartItemError-${line}`]
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`]

    if (!(cartItemError instanceof HTMLElement))
      throw new Error('Cart item error not found')
    if (!(cartItemErrorContainer instanceof HTMLElement))
      throw new Error('Cart item error container not found')

    cartItemError.textContent = parsedResponseText.errors
    cartItemErrorContainer.classList.remove('hidden')
  }

  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false })
      return
    }
    if (event.target === this) return

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId]
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml)
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false })
    }
  }

  connectedCallback() {
    super.connectedCallback()

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate)
    document.addEventListener(
      ThemeEvents.discountUpdate,
      this.handleDiscountUpdate
    )
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#queueLineChange
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate)
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#queueLineChange
    )
    document.removeEventListener(
      ThemeEvents.discountUpdate,
      this.handleDiscountUpdate
    )
    this.#clearPendingLineChanges()
  }

  #clearPendingLineChanges() {
    for (const timeout of this.#pendingLineChanges.values()) {
      clearTimeout(timeout)
    }
    this.#pendingLineChanges.clear()
  }

  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail

    if (!line) return

    if (quantity === 0) {
      this.onLineItemRemove(line)
      return
    }

    this.updateQuantity({
      action: 'change',
      line,
      quantity
    })
  }

  onLineItemRemove(line) {
    this.updateQuantity({
      action: 'clear',
      line,
      quantity: 0
    })

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1]

    if (!cartItemRowToRemove) return

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter(
        (row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key
      )
    ]

    rowsToRemove.forEach((row) => {
      row.remove()
    })
  }

  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`
    )

    const customCursor = document.querySelector('custom-cursor')
    customCursor?.setLoading?.(2000)

    const { line, quantity } = config

    const cartItemsComponents = document.querySelectorAll('cart-items')
    const sectionsToUpdate = new Set([this.sectionId])
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId)
      }
    })

    const extraSelectors = ['cart-rewards', 'cart-discount', 'cart-summary']
    document.querySelectorAll(extraSelectors.join(',')).forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionsToUpdate.add(el.dataset.sectionId)
      }
    })

    const body = JSON.stringify({
      line,
      quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname
    })

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text()
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText)

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText)
          return
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        )

        const newCartHiddenItemCount = newSectionHTML.querySelector(
          '[ref="cartItemCount"]'
        )?.textContent
        const newCartItemCount = newCartHiddenItemCount
          ? Number.parseInt(newCartHiddenItemCount, 10)
          : 0

        this.dispatchEvent(
          new CartUpdateEvent(
            parsedResponseText,
            `cart-items-${this.sectionId}`,
            {
              itemCount: newCartItemCount,
              sections: parsedResponseText.sections,
              source: 'cart-items'
            }
          )
        )

        morphSection(
          this.sectionId,
          parsedResponseText.sections[this.sectionId]
        )
      })
      .catch(() => undefined)
      .finally(() => {
        customCursor?.clearLoading?.()
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker)
      })
  }

  get sectionId() {
    const { sectionId } = this.dataset

    if (!sectionId) throw new Error('Section id missing')

    return sectionId
  }
}

if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItemsComponent)
}
